/**
 * Facet field for determining the data type of a LatestValue.
 */
export type LatestValueKind = 'number' | 'boolean' | 'enum' | 'null';

/**
 * Local TypeScript type for reading events, values, and snapshots (as in alarm activations).
 * This type is
 */
export interface LatestValue {
  pointId: string;
  timestamp: Date;
  kind: LatestValueKind;
  number?: number;
  boolean?: boolean;
  enum?: string;
}

export type ReadingEventType = 'keepalive' | 'reading';

export interface ReadingEvent {
  type: ReadingEventType;
  latestValue?: LatestValue;
}

/**
 * Convert a CompactReadingEvent to individual ReadingEvents
 */
export function convertCompactReadingEventToReadingEvents(compactEvent: {
  type?: string;
  timestamp: Date;
  numbers?: { [k: string]: number };
  booleans?: { [k: string]: boolean };
  enums?: { [k: string]: string };
  nulls?: string[];
}): ReadingEvent[] {
  const events: ReadingEvent[] = [];

  // Handle keepalive events
  if (compactEvent.type === 'keepalive') {
    return [{ type: 'keepalive' }];
  }

  // Process number readings
  if (compactEvent.numbers) {
    Object.entries(compactEvent.numbers).forEach(([pointId, value]) => {
      events.push({
        type: 'reading',
        latestValue: {
          pointId,
          timestamp: compactEvent.timestamp,
          kind: 'number',
          number: value,
        },
      });
    });
  }

  // Process boolean readings
  if (compactEvent.booleans) {
    Object.entries(compactEvent.booleans).forEach(([pointId, value]) => {
      events.push({
        type: 'reading',
        latestValue: {
          pointId,
          timestamp: compactEvent.timestamp,
          kind: 'boolean',
          boolean: value,
        },
      });
    });
  }

  // Process enum readings
  if (compactEvent.enums) {
    Object.entries(compactEvent.enums).forEach(([pointId, value]) => {
      events.push({
        type: 'reading',
        latestValue: {
          pointId,
          timestamp: compactEvent.timestamp,
          kind: 'enum',
          enum: value,
        },
      });
    });
  }

  // Process null readings
  if (compactEvent.nulls) {
    compactEvent.nulls.forEach((pointId) => {
      events.push({
        type: 'reading',
        latestValue: {
          pointId,
          timestamp: compactEvent.timestamp,
          kind: 'null',
        },
      });
    });
  }

  return events;
}

/**
 * Get the concrete value from a LatestValue in a type-safe way.
 */
export function getLatestPrimitiveValue(
  latestValue: LatestValue | undefined
): number | boolean | string | undefined {
  if (!latestValue) return undefined;
  switch (latestValue.kind) {
    case 'number':
      return latestValue.number;
    case 'boolean':
      return latestValue.boolean;
    case 'enum':
      return latestValue.enum;
    case 'null':
    default:
      return undefined;
  }
}
